import { useCallback } from 'react';
import { X } from 'lucide-react';
import styles from './ScenarioGenerator.module.css';
import { Tooltip } from '../../common/Tooltip';
import { GenerateButton } from '../common/GenerateButton';
import { type VideoJobRestorePayload } from './VideoJobHistoryPanel';
import { brandConfig } from '../../../config/brands';
import type { GenerationAsset, VideoGenerationMode } from '../state/useGenerationState';
import { DEFAULT_VIDEO_SETTINGS } from '../state/types';
import type { MentionActorSource } from '../state/mentionUtils';
import type { Scenario } from './types';
import type { UseProductSelectionReturn } from './hooks/useProductSelection';
import type { UseSetupAccordionsReturn } from './hooks/useSetupAccordions';
import type { UsePortalDropdownReturn } from './hooks/usePortalDropdown';
import type { MotionLayeredPromptState } from './hooks/useVideoHandlers';
import { useScenarioSetup } from './hooks/ScenarioSetupContext';
import { STORAGE_KEY_PRODUCT_NAME, STORAGE_KEY_BRAND_NAME, STORAGE_KEY_DURATION } from './constants';
import { ProductSection } from './setup/ProductSection';
import { PromptModePanel } from './setup/PromptModePanel';
import { CastingSection } from './setup/CastingSection';
import { ScenarioSettingsSection } from './setup/ScenarioSettingsSection';
import { FirstFrameSettingsSection } from './setup/FirstFrameSettingsSection';
import { VideoSettingsSection } from './setup/VideoSettingsSection';

export type IncludeOption = 'include' | 'exclude' | 'generate_only' | 'unspecified';
export type IncludeNarrationOption = 'include' | 'exclude' | 'unspecified';

interface MutationLike<TVariables = Record<string, unknown>> {
  isPending: boolean;
  isError: boolean;
  error?: unknown;
  /** 直近に mutate() へ渡された引数 (react-query UseMutationResult.variables)。
   *  PromptModePanel の「SEカタログ由来の参照音声が利用不可」確認ダイアログ
   *  (docs/features/reference-audio-roles-plan.md §2.7) が、直近送信ペイロードを
   *  起点に該当音声だけ除いて再送するために参照する。 */
  variables?: unknown;
  mutate: (variables: TVariables) => void;
  reset: () => void;
}

/**
 * SetupPanel / 配下セクションの props。
 *
 * フォームの値・setter・選択肢・派生表示は ScenarioSetupContext
 * (useScenarioSetup) 経由で受け取るため、ここには親のオーケストレーション
 * (送信・生成実行・モーダル開閉)、フック オブジェクト、プロンプトモードの
 * 出力周りだけを残している。
 */
export interface SetupPanelProps {
  // 画面遷移 / 一覧
  scenarios: Scenario[];
  changePhase: (next: 'setup' | 'work') => void;
  hasTransitioned: boolean;

  // フック
  product: UseProductSelectionReturn;
  accordions: UseSetupAccordionsReturn;
  frameDropdown: UsePortalDropdownReturn;

  // ミューテーション
  // シナリオ生成 mutation の variables は {payload, clientJobId} (動画生成とは別形)。
  // この配下では mutate を呼ばないので never で受ける (isPending / isError だけ使う)。
  mutation: MutationLike<never>;
  videoGenerationMutation: MutationLike;
  handleSubmit: (e: React.FormEvent) => void;

  // 商品関連 UI 補助
  productSelectRef: React.RefObject<HTMLButtonElement | null>;
  showProductTooltip: boolean;
  setShowProductTooltip: React.Dispatch<React.SetStateAction<boolean>>;
  setIsScenarioBookOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsProductSelectorOpen: React.Dispatch<React.SetStateAction<boolean>>;
  productImageFetchError: string | null;
  setProductImageFetchError: React.Dispatch<React.SetStateAction<string | null>>;
  productImageFetching: boolean;
  clearAssets: () => void;
  selectedAssets: GenerationAsset[];
  /** 素材 @メンション候補 (docs/features/material-at-mention-plan.md レビュー指摘 P1)。
   * selectedAssets のうち、動画生成の参照上限で実際には送信されない素材を除いたもの。
   * PromptModePanel の動画プロンプト欄ポップアップが使う。 */
  mentionEligibleAssets: GenerationAsset[];
  /** AI俳優 @メンション候補 (同「俳優メンション」節)。casting/seedancePreset は排他で
   * 常に候補になる (参照上限フィルタ対象外)。PromptModePanel の動画プロンプト欄
   * ポップアップが使う。 */
  mentionActorSources: MentionActorSource[];
  asinDisabled: boolean;
  customDisabled: boolean;
  promptDisabled: boolean;

  /** 動画生成モードの切替 (非対応素材があるときは確認モーダルを挟むガード付き)。 */
  handleVideoGenerationModeChange: (next: VideoGenerationMode) => void;

  // プロンプトモード
  promptTextareaRef: React.RefObject<HTMLTextAreaElement | null>;
  isVideoCreditInsufficient: boolean;
  handleGenerateVideoFromPrompt: () => Promise<void> | void;
  /** 3 タブ目「モーショングラフィックス」の料金・押せるか・生成 (useVideoHandlers・motion-graphics-mode-plan.md §5.3・§5.4) */
  motionLayeredPrompt: MotionLayeredPromptState;
  promptVideoGenerated: boolean;
  promptVideoMaterialId: string | null;
  promptVideoDisplayRef: React.RefObject<HTMLDivElement | null>;
  tempMaterials: any;
  cloudMaterials: any;
  setPreviewModalMaterial: (m: any | null) => void;
  handleVideoHistoryPreview: (materialId: string) => void;
  handleRestoreVideoSettings: (payload: VideoJobRestorePayload) => void;
  openPurchaseModal: () => void;
  resolveErrorMessage: (error: unknown) => string;
  showVideoPromptHistory: boolean;
  setShowVideoPromptHistory: React.Dispatch<React.SetStateAction<boolean>>;
  handleSelectVideoPromptHistory: (prompt: string) => void;

  // 信用度/Submit
  isScenarioSubmitDisabled: boolean;
  isRegenerate: boolean;
  scenarioGuidanceMessages: React.ReactElement[];
  submitRowRef: React.RefObject<HTMLDivElement | null>;
  isStorageExceeded: boolean;

  // マニュアル商品プロンプト
  showManualProductPrompt: boolean;
  handleOpenManualProductForm: () => Promise<void> | void;
}

export function SetupPanel(props: SetupPanelProps) {
  const {
    scenarios, changePhase, hasTransitioned,
    product, accordions, frameDropdown,
    mutation, handleSubmit,
    setIsScenarioBookOpen,
    handleGenerateVideoFromPrompt,
    isScenarioSubmitDisabled, isRegenerate, scenarioGuidanceMessages, submitRowRef,
    showManualProductPrompt, handleOpenManualProductForm,
  } = props;

  const {
    mode, scenarioCreditCost,
    setTone, setDuration, durationUserEditedRef, modelAwareDurationDefault,
    setCharacterCountRaw, characterCountUserEditedScenarioRef,
    setIncludeNarration, setIncludeDialogue, setIncludeTelop,
    setExcludePeople, setAdditionalPrompt, setVisualStyle,
    setSelectedCasting, setAdditionalCastings,
    setSelectedSeedancePresetActor, setAdditionalSeedancePresetActors,
    setCastingGenderFilter, setCastingAgeFilter,
    setVideoSettings,
    setScenarioCount, scenarioCountOptions,
    setScenarioModel, DEFAULT_SCENARIO_MODEL,
    setAutoGenerateNarration,
    setVideoGenerationMode,
    setAutoGenerateFirstFrame, setAutoGenerateStoryboard,
    selectScenarioImageModel, GENERATION_MODEL_SPEED,
    isMotionEngine,
  } = useScenarioSetup();

  const {
    setAsin, setManualProductId, setManualProductTitle, setManualProductImageUrl,
    setProductName, setBrandName, setSellingPoints, setGoal, setTargetAudience, setPricePoint,
  } = product;
  const { clearAssets } = props;

  // すべてリセット (静止画・属性選択モードの handleResetAll と同趣旨)。
  // コンテンツ系の選択・動画生成設定 (モデル/解像度/アスペクト比等)・商品の選択・
  // 選択素材 (selectedAssets)・シナリオ案数・シナリオAIモデル・自動生成トグル
  // (ナレーション/ストーリーボード/フレーム)・参考画像アコーディオン (制作方式/
  // フレーム画像生成モデル) を初期値へ戻す。思考レベルのみリセット対象外。
  // localStorage は useLocalStorageSync ほか各 setter の片方向同期が追従して上書きする。
  const handleResetAll = useCallback(() => {
    // 商品の選択: ASIN / 登録商品 / カスタム入力欄をクリア。ASIN の localStorage は
    // useProductSelection の同期 effect が削除する。商品名/ブランド名の localStorage は
    // 「値があるときだけ保存」の片方向同期でリロード時に復活するため、ここで明示削除する。
    setAsin('');
    setManualProductId(null);
    setManualProductTitle(null);
    setManualProductImageUrl(null);
    setProductName('');
    setBrandName('');
    setSellingPoints('');
    setGoal('');
    setTargetAudience('');
    setPricePoint('');
    localStorage.removeItem(STORAGE_KEY_PRODUCT_NAME);
    localStorage.removeItem(STORAGE_KEY_BRAND_NAME);
    // 選択素材: 現在スコープ (新規作成 setup) の selectedAssets を空にする
    clearAssets();
    // 動画生成設定: SD2.5標準 / 720p / 16:9 / 4秒 / 音声あり (DEFAULT_VIDEO_SETTINGS)。
    // 想定尺の既定スナップ effect がモデル変更に追従して再計算する。
    setVideoSettings(DEFAULT_VIDEO_SETTINGS);
    setVisualStyle('photorealistic');
    setTone('');
    // 想定尺: ユーザー編集フラグを下ろしてモデル追従の既定値へ戻す
    // (以降は useScenarioFormState の既定スナップ effect が追従を再開する)。
    // 保存値も消し、次回起動時もモデル追従の既定値から始める。
    durationUserEditedRef.current = false;
    setDuration(modelAwareDurationDefault);
    localStorage.removeItem(STORAGE_KEY_DURATION);
    // 登場人物数: raw setter で「指定なし」へ (ユーザー操作扱いにせず、超過俳優の
    // 自動解除トースト経由ではなく下の明示クリアで俳優を外す)。
    setCharacterCountRaw('');
    characterCountUserEditedScenarioRef.current = null;
    setIncludeNarration('unspecified');
    setIncludeDialogue('unspecified');
    setIncludeTelop('generate_only');
    setExcludePeople(false);
    setAdditionalPrompt('');
    // キャスティング: AI俳優 / Seedance preset / 絞り込みフィルターをすべて解除
    setSelectedCasting(null);
    setAdditionalCastings([]);
    setSelectedSeedancePresetActor(null);
    setAdditionalSeedancePresetActors([]);
    setCastingGenderFilter('none');
    setCastingAgeFilter(null);
    // シナリオ案数: 既定2案 (partner の max_scenario_count が2未満ならその上限に丸める)
    const maxScenarioCount = scenarioCountOptions[scenarioCountOptions.length - 1] ?? 1;
    setScenarioCount(Math.min(2, maxScenarioCount));
    // シナリオ生成AIモデル: 既定 (ランダム)
    setScenarioModel(DEFAULT_SCENARIO_MODEL);
    // すべてのナレーションを自動生成: オフ
    setAutoGenerateNarration(false);
    // 参考画像アコーディオン: 制作方式=絵コンテから制作 / 各自動生成チェック=オフ /
    // フレーム画像生成モデル=Gemini標準
    setVideoGenerationMode('storyboard');
    setAutoGenerateStoryboard(false);
    setAutoGenerateFirstFrame(false);
    selectScenarioImageModel(GENERATION_MODEL_SPEED);
  }, [
    setVisualStyle, setTone, durationUserEditedRef, setDuration, modelAwareDurationDefault,
    setCharacterCountRaw, characterCountUserEditedScenarioRef,
    setIncludeNarration, setIncludeDialogue, setIncludeTelop,
    setExcludePeople, setAdditionalPrompt,
    setSelectedCasting, setAdditionalCastings,
    setSelectedSeedancePresetActor, setAdditionalSeedancePresetActors,
    setCastingGenderFilter, setCastingAgeFilter, setVideoSettings,
    setAsin, setManualProductId, setManualProductTitle, setManualProductImageUrl,
    setProductName, setBrandName, setSellingPoints, setGoal, setTargetAudience, setPricePoint,
    clearAssets,
    setScenarioCount, scenarioCountOptions, setScenarioModel, DEFAULT_SCENARIO_MODEL,
    setAutoGenerateNarration, setVideoGenerationMode,
    setAutoGenerateStoryboard, setAutoGenerateFirstFrame,
    selectScenarioImageModel, GENERATION_MODEL_SPEED,
  ]);

  const { setManualProductPromptDismissed } = product;

  const { openAllAccordions, closeAllAccordions, areAllAccordionsOpen } = accordions;

  void frameDropdown; // 親で portal を描画するため SetupPanel では使わないが props で受け取る


  return (
          <div className={`${styles.setupPanel} ${hasTransitioned ? styles.setupPanelEnter : ''}`}>

      {mode !== 'prompt' && (
      <div className={styles.setupHeader}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0, color: 'rgba(245, 241, 230, 0.95)' }}>{brandConfig.labels.scenarioSettings}</h2>
        {scenarios.length > 0 && (
          <button
            type="button"
            className={`${styles.backToSetupButton} ${styles.navButton}`}
            onClick={() => changePhase('work')}
          >
            編集に戻る
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
          </button>
        )}
      </div>
      )}

      {/* アコーディオン開閉コントロール（右寄せ） */}
      {mode !== 'prompt' && (
        <div className="gen-accordion-controls" style={{ justifyContent: 'space-between' }}>
          <Tooltip content="シナリオブックを開く">
            <button
              type="button"
              className={styles.openBookTab}
              onClick={() => setIsScenarioBookOpen(true)}
            >
              <img src="/book_book_pages.svg" alt="" className={styles.openBookTabIcon} style={{ width: '18px', height: '18px' }} />
              <span className={styles.openBookTabLabel}>開く</span>
            </button>
          </Tooltip>
          <div className={styles.iconButtonGroup}>
            <Tooltip content="すべてリセット">
              <button
                type="button"
                className={styles.iconButton}
                onClick={handleResetAll}
              >
                <X size={16} />
              </button>
            </Tooltip>
            {/* すべて開く ⇔ すべて閉じる の1ボタントグル (全セクションが開いていれば「閉じる」) */}
            <Tooltip content={areAllAccordionsOpen ? 'すべて閉じる' : 'すべて開く'}>
              <button
                type="button"
                className={styles.iconButton}
                onClick={areAllAccordionsOpen ? closeAllAccordions : openAllAccordions}
              >
                {areAllAccordionsOpen ? (
                  <img src="/move_collapse_vertical.svg" alt="すべて閉じる" style={{ width: '16px', height: '16px' }} />
                ) : (
                  <img src="/move_expand_vertical.svg" alt="すべて開く" style={{ width: '16px', height: '16px' }} />
                )}
              </button>
            </Tooltip>
          </div>
        </div>
      )}

      <form className={styles.form} onSubmit={mode === 'prompt' ? (e) => { e.preventDefault(); handleGenerateVideoFromPrompt(); } : handleSubmit}>
        {mode !== 'prompt' ? (
          <>
          <ProductSection {...props} />
          </>
        ) : (
          <>
            <PromptModePanel {...props} />
          </>
        )}

        {/* キャスティングセクション */}
        {mode !== 'prompt' && (
          <CastingSection {...props} />
        )}

        {/* ASINモードとカスタムモードの共通シナリオ生成設定 */}
        {mode !== 'prompt' && (
          <ScenarioSettingsSection {...props} />
        )}

      {mode !== 'prompt' && (
        <>
      {/* 最初のフレーム設定（シナリオ生成前から表示）。全編モーション (§10.5) はフレーム画像・絵コンテを使わないので出さない */}
        {isMotionEngine ? (
          <div style={{ fontSize: 12, color: 'rgba(245, 241, 230, 0.55)', margin: '4px 0 8px' }}>
            全編をモーショングラフィックスで作るため、フレーム画像・絵コンテは使いません。生成ボタンの後にBGMを先に決めます。
          </div>
        ) : (
          <FirstFrameSettingsSection {...props} />
        )}

      {/* 動画生成設定（シナリオ生成前から表示） */}
        <VideoSettingsSection {...props} />

      {/* シナリオを生成ボタン（動画生成設定の下） */}
        <div ref={submitRowRef} className={styles.submitRow}>
          <GenerateButton
            type="submit"
            label={isRegenerate ? brandConfig.labels.scenarioRegenerate : brandConfig.labels.scenarioGenerate}
            creditCost={scenarioCreditCost}
            onClick={handleSubmit}
            isLoading={mutation.isPending}
            loadingLabel={`${brandConfig.labels.scenarioGenerate}中…`}
            disabled={isScenarioSubmitDisabled}
          />
        </div>
        {scenarioGuidanceMessages.length > 0 && (
          <div className={styles.generateGuidance}>
            {scenarioGuidanceMessages}
          </div>
        )}

        {showManualProductPrompt && (
          <div className={styles.manualProductPromptOverlay}>
            <div className={styles.manualProductPromptModal}>
              <p className={styles.manualProductPromptText}>
                入力した商品情報を商品データとして登録して、再利用可能にしますか？
              </p>
              <div className={styles.manualProductPromptButtons}>
                <button type="button" className={styles.manualProductPromptYes} onClick={handleOpenManualProductForm}>
                  登録する
                </button>
                <button type="button" className={styles.manualProductPromptNo} onClick={() => setManualProductPromptDismissed(true)}>
                  今はしない
                </button>
              </div>
            </div>
          </div>
        )}

        </>
      )}
      </form>

      {/* SAITE のみ: ページ末端にもシナリオブックの「開く」リンクを設置（主導線ではない控えめデザイン） */}
      {mode !== 'prompt' && brandConfig.id === 'hypeapp' && (
        <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: '12px' }}>
          <Tooltip content="シナリオブックを開いて過去に作成した素材を編集する">
            <button
              type="button"
              className={styles.openBookLink}
              onClick={() => setIsScenarioBookOpen(true)}
            >
              <img src="/book_book_pages.svg" alt="" className={styles.openBookLinkIcon} />
              <span>開く</span>
            </button>
          </Tooltip>
        </div>
      )}

          </div>
  );
}
